import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Badge } from '@/shared/ui/badge';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import {
  User,
  Activity,
  Globe,
  Code,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ChevronDown,
  Server,
  FileText,
  Info,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { AuditLog, getActionBadge, getResourceTypeBadge, formatCreatedAt, getMethodBadge } from '@/shared/types/auditLogTypes';
import { showSuccess } from '@/shared/utils/toast-utils';
import { useState } from 'react';

interface AuditLogDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditLog: AuditLog | null;
}

export default function AuditLogDetailsModal({ open, onOpenChange, auditLog }: AuditLogDetailsModalProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  if (!auditLog) return null;

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const actionBadge = getActionBadge(auditLog.action);
  const methodBadge = getMethodBadge(auditLog.metadata?.request?.method || '');
  const resourceTypeBadge = getResourceTypeBadge(auditLog.resourceType);
  const isSuccess = (auditLog.metadata?.response?.statusCode !== null && auditLog.metadata?.response?.statusCode >= 200 && auditLog.metadata?.response?.statusCode < 300);

  const copyToClipboard = (text: string, label: string = 'Content') => {
    navigator.clipboard.writeText(text);
    showSuccess(`${label} copied to clipboard`);
  };

  const formatJson = (obj: any) => {
    if (obj instanceof Map) {
      const mapObj = Object.fromEntries(obj);
      return JSON.stringify(mapObj, null, 2);
    }

    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return obj;
      }
    }

    return JSON.stringify(obj, null, 2);
  };

  const getStatusIcon = () => {
    if (isSuccess) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (auditLog.metadata?.response?.statusCode !== null && auditLog.metadata?.response?.statusCode >= 400 && auditLog.metadata?.response?.statusCode < 500) {
      return <AlertCircle className="h-5 w-5 text-amber-500" />;
    } else if (auditLog.metadata?.response?.statusCode !== null && auditLog.metadata?.response?.statusCode >= 500) {
      return <XCircle className="h-5 w-5 text-red-500" />;
    }
    return <Clock className="h-5 w-5 text-gray-500" />;
  };

  const getStatusBadgeVariant = () => {
    if (isSuccess) return 'default';
    if (auditLog.metadata?.response?.statusCode !== null && auditLog.metadata?.response?.statusCode >= 400 && auditLog.metadata?.response?.statusCode < 500) return 'secondary';
    if (auditLog.metadata?.response?.statusCode !== null && auditLog.metadata?.response?.statusCode >= 500) return 'destructive';
    return 'outline';
  };

  const tabCount = auditLog.metadata?.request && auditLog.metadata?.response ? 4 : auditLog.metadata?.request ? 3 : auditLog.metadata?.response ? 2 : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b bg-gradient-to-r from-background to-muted/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Activity className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Audit Log Details</DialogTitle>
                <DialogDescription className="mt-1">
                  {formatCreatedAt(auditLog.createdAt)}
                </DialogDescription>
              </div>
            </div>
            {auditLog.metadata?.response?.statusCode !== null && (
              <div className="flex items-center gap-2 p-2">
                {getStatusIcon()}
                <Badge variant={getStatusBadgeVariant()} className="font-mono font-semibold">
                  {auditLog.metadata?.response?.statusCode}
                </Badge>
              </div>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className={`mx-6 mt-4 mb-0 grid w-full max-w-md grid-cols-${tabCount} shrink-0 w-full justify-center mx-auto`}>
            <TabsTrigger value="overview" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
              <Info className="h-4 w-4" />
              Overview
            </TabsTrigger>
            {auditLog.metadata?.request && (
              <TabsTrigger value="request" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Request
              </TabsTrigger>
            )}
            {auditLog.metadata?.response && (
              <TabsTrigger value="response" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
                <Server className="h-4 w-4" />
                Response
              </TabsTrigger>
            )}
            <TabsTrigger value="additional" className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2">
              <Code className="h-4 w-4" />
              Additional
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 px-6 pb-6">
            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* Quick Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {auditLog.userId && (
                  <div className="rounded-lg border bg-card p-4 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4" />
                      User
                    </div>
                    <p className="font-medium text-sm">{auditLog.emailId || auditLog.userId}</p>
                    <p className="text-xs">{auditLog.role}</p>
                  </div>
                )}

                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Activity className="h-4 w-4" />
                    Action
                  </div>
                  <Badge className={actionBadge.className}>
                    {actionBadge.label}
                  </Badge>
                </div>

                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4" />
                    Resource Type
                  </div>
                  <Badge className={resourceTypeBadge.className}>
                    {resourceTypeBadge.label}
                  </Badge>
                </div>
              </div>

              {/* Request Summary */}
              <div className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2 font-semibold">
                  <Globe className="h-4 w-4" />
                  Request Summary
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium uppercase tracking-wide">Method</label>
                    <div>
                      <Badge className={methodBadge.className}>
                        {methodBadge.label}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium uppercase tracking-wide">Content Type</label>
                    <p className="text-sm">{auditLog.metadata?.request?.contentType || 'N/A'}</p>
                  </div>
                  {auditLog.metadata?.request?.uri && (
                    <div className="space-y-1 md:col-span-2">
                      <label className="text-xs font-medium uppercase tracking-wide">URI</label>
                      <div className="flex items-start gap-2">
                        <p className="text-sm font-mono break-all break-words overflow-wrap-anywhere flex-1">
                          {auditLog.metadata?.request?.uri}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(auditLog.metadata?.request?.uri || '', 'URI')}
                          className="h-8 w-8 p-0 flex-shrink-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {auditLog.metadata?.request?.remoteAddr && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium uppercase tracking-wide">Remote Address</label>
                      <p className="text-sm font-mono">{auditLog.metadata?.request?.remoteAddr}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="request" className="mt-4 space-y-4">
              {/* Request Headers */}
              {auditLog.metadata?.request?.headers && Object.keys(auditLog.metadata?.request?.headers).length > 0 && (
                <div className="rounded-lg border bg-card">
                  <Collapsible open={openSections.headers} onOpenChange={() => toggleSection('headers')}>
                    <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 font-semibold">
                        <FileText className="h-4 w-4" />
                        Request Headers
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(formatJson(auditLog.metadata?.request?.headers || {}), 'Request Headers');
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSections.headers ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4">
                        <div className="bg-muted/50 rounded-md p-3 overflow-x-auto min-w-0">
                          <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">
                            {formatJson(auditLog.metadata?.request?.headers || {})}
                          </pre>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}

              {/* Path Variables */}
              {auditLog.metadata?.request?.pathVariables && Object.keys(auditLog.metadata?.request?.pathVariables).length > 0 && (
                <div className="rounded-lg border bg-card">
                  <Collapsible open={openSections.pathVars} onOpenChange={() => toggleSection('pathVars')}>
                    <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 font-semibold">
                        <Code className="h-4 w-4" />
                        Path Variables
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(formatJson(auditLog.metadata?.request?.pathVariables || {}), 'Path Variables');
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSections.pathVars ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4">
                        <div className="bg-muted/50 rounded-md p-3 overflow-x-auto min-w-0">
                          <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">
                            {formatJson(auditLog.metadata?.request?.pathVariables || {})}
                          </pre>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}

              {/* Query String */}
              {auditLog.metadata?.request?.queryString && (
                <div className="rounded-lg border bg-card p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-semibold">
                      <ExternalLink className="h-4 w-4" />
                      Query String
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(auditLog.metadata?.request?.queryString || '', 'Query String')}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="bg-muted/50 rounded-md p-3">
                    <p className="text-sm font-mono break-all break-words overflow-wrap-anywhere">
                      {auditLog.metadata?.request?.queryString}
                    </p>
                  </div>
                </div>
              )}

              {/* Request Body */}
              {auditLog.metadata?.request?.body && Object.keys(auditLog.metadata?.request?.body).length > 0 && (
                <div className="rounded-lg border bg-card">
                  <Collapsible open={openSections.requestBody} onOpenChange={() => toggleSection('requestBody')}>
                    <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 font-semibold">
                        <FileText className="h-4 w-4" />
                        Request Body
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(formatJson(auditLog.metadata?.request?.body || {}), 'Request Body');
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSections.requestBody ? 'rotate-180' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4">
                        <div className="bg-muted/50 rounded-md p-3 overflow-x-auto min-w-0">
                          <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">
                            {formatJson(auditLog.metadata?.request?.body || {})}
                          </pre>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
            </TabsContent>

            <TabsContent value="response" className="mt-4 space-y-4">
              {/* Response Status */}
              <div className="rounded-lg border bg-card p-4 space-y-4">
                <div className="flex items-center gap-2 font-semibold">
                  {getStatusIcon()}
                  Response Status
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium uppercase tracking-wide">Status Code</label>
                    <div>
                      <Badge variant={getStatusBadgeVariant()}>{auditLog.metadata?.response?.statusCode || '-'}</Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Body */}
              <div className="rounded-lg border bg-card">
                <Collapsible open={openSections.responseBody} onOpenChange={() => toggleSection('responseBody')}>
                  <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 font-semibold">
                      <FileText className="h-4 w-4" />
                      Response Body
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(formatJson(auditLog.metadata?.response?.body || {}), 'Response Body');
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSections.responseBody ? 'rotate-180' : ''}`} />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4">
                      <div className="bg-muted/50 rounded-md p-3 overflow-x-auto min-w-0">
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">
                          {formatJson(auditLog.metadata?.response?.body || {})}
                        </pre>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </TabsContent>

            <TabsContent value="additional" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* User Agent */}
                {auditLog.userAgent && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Globe className="h-4 w-4" />
                      User Agent
                    </div>
                    <p className="text-sm break-words">{auditLog.userAgent}</p>
                  </div>
                )}

                {/* Remote Address */}
                {auditLog.metadata?.request?.remoteAddr && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Globe className="h-4 w-4" />
                      Remote Address
                    </div>
                    <p className="text-sm break-words">Address: {auditLog.metadata?.request?.remoteAddr}</p>
                  </div>
                )}

                {/* User */}
                {auditLog.userId && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <User className="h-4 w-4" />
                      User
                    </div>
                    <p className="text-sm break-words">{auditLog.userId}</p>
                    <p className="text-xs">{auditLog.role}</p>
                  </div>
                )}

                {/* Organization ID */}
                {auditLog.organizationId && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Code className="h-4 w-4" />
                      Organization
                    </div>
                    <p className="text-sm break-words">{auditLog.organizationId}</p>
                  </div>
                )}

                {/* Service Line Number */}
                {auditLog.serviceLineNumber?.length > 0 && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Code className="h-4 w-4" />
                      Service Line Number
                    </div>
                    <p className="text-sm break-words">{auditLog.serviceLineNumber}</p>
                  </div>
                )}

                {/* Kit Numbers */}
                {auditLog.kitNumbers?.length > 0 && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Code className="h-4 w-4" />
                      Kit Numbers
                    </div>
                    <p className="text-sm break-words">{auditLog.kitNumbers}</p>
                  </div>
                )}

                {/* Base Subscription ID */}
                {auditLog.baseSubscriptionId && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Code className="h-4 w-4" />
                      Subscription Plan
                    </div>
                    <p className="text-sm break-words">Data: {auditLog.baseSubscriptionData}</p>
                    <p className="text-xs">ID: {auditLog.baseSubscriptionId}</p>
                  </div>
                )}

                {/* Overage Data */}
                {auditLog.overageData && (
                  <div className="rounded-lg border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Activity className="h-4 w-4" />
                      Overage Data
                    </div>
                    <p className="text-sm break-words">Data: {auditLog.overageData}</p>
                    <p className="text-xs">End Date: {auditLog.overageEndDate}</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
